import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/layout/Layout';
import { useAuth } from '@/context/auth';
import { getSugarDaddyProfileDetails } from '@/services/sugar-profile.service';
import { SugarDaddyProfile, User } from '@/types';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Terminal, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { canContactSugarDaddy } from '@/context/auth/auth-utils';
import { useLanguageUtils } from '@/context/language-context';

const SugarDaddyDetailPage: React.FC = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const { t } = useTranslation(['sugar', 'common']);
  const { user } = useAuth(); // Current logged-in user
  const navigate = useNavigate(); // Add useNavigate
  const { getUrlWithLanguage } = useLanguageUtils(); // Add language utils
  const [profile, setProfile] = useState<(SugarDaddyProfile & { user: Partial<User> }) | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [canContact, setCanContact] = useState(false);

  useEffect(() => {
    const fetchProfileAndCheckPermissions = async () => {
      if (!profileId) {
        setError('Profile ID is missing');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      setProfile(null);
      setCanContact(false);

      try {
          const result = await getSugarDaddyProfileDetails(profileId);
          if (result.success) {
            setProfile(result.data);
            // Check permission if viewer is a potential SB
            if (user && (user.role === 'escort' || user.role === 'sugar_baby')) {
                 setCanContact(canContactSugarDaddy(user, result.data?.user?.id, result.data?.user?.subscriptionTier));
            }
          } else {
            setError(result.error?.message || t('common:error_unknown'));
          }
       } catch (err: any) {
           setError(err.message || t('common:error_unexpected'));
       } finally {
           setIsLoading(false);
       }
    };
    fetchProfileAndCheckPermissions();
  }, [profileId, t, user]); // Add user dependency

  const handleContact = () => {
      if (!profile || !profile.user?.id) return;
       // Navigate to messages page, passing recipient ID in state
      navigate(getUrlWithLanguage('/messages'), { 
          state: { recipientId: profile.user.id, recipientPseudo: profile.pseudo }
      });
  };

  const renderVerificationBadge = (status: string | undefined) => {
      if (status === 'fully_verified') {
          return <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1"/> {t('sugar:badge_verified_sd')}</span>;
      } else if (status === 'revenue_verified') {
          // Add a specific badge if revenue verification is implemented and distinct
          return <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><CheckCircle className="w-3 h-3 mr-1"/> {t('sugar:badge_revenue_verified')}</span>;
      }
      // Optionally show badges for other states like 'pending_id', 'id_verified'?
      return null;
  }

  const renderProfileDetails = () => {
    if (!profile) return null;
    
    const photos = profile.photos?.gallery || (profile.photos?.main ? [profile.photos.main] : []);

    return (
      <div className="space-y-6">
        {/* Basic Photo Display */}
        {photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {photos.map((photoUrl, index) => (
                    <img key={index} src={photoUrl} alt={`${profile.pseudo} photo ${index+1}`} className="rounded-lg object-cover aspect-square"/>
                ))}
            </div>
        ) : (
            <Skeleton className="w-full h-64 rounded-lg bg-muted" /> 
        )}
        
        <div className="flex items-center">
            <h2 className="text-2xl font-semibold">{profile.pseudo}</h2>
            {renderVerificationBadge(profile.verificationStatus)}
        </div>
        <p className="text-muted-foreground">{profile.location?.city}, {profile.location?.country}</p>
        <p className="text-sm">{t('sugar:age_range')}: {profile.ageRange}</p>
        {profile.profession && <p className="text-sm">{t('sugar:profession')}: {profile.profession}</p>}
        
        <div>
          <h3 className="font-semibold mb-2">{t('sugar:section_description')}</h3>
          <p className="text-sm whitespace-pre-wrap">{profile.description}</p>
        </div>

        <div>
          <h3 className="font-semibold mb-2">{t('sugar:section_lifestyle')}</h3>
          <div className="flex flex-wrap gap-2">
            {profile.lifestyleInterests?.map(tag => (
              <span key={tag} className="bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded">{t(`sugar:tags.lifestyle.${tag}`, tag)}</span>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-semibold mb-2">{t('sugar:section_offered_arrangement')}</h3>
           <ul className="list-disc list-inside text-sm space-y-1">
             <li>{t('sugar:offered_support_type')}: {profile.offeredArrangement?.supportType?.map(tag => t(`sugar:tags.sd_support.${tag}`, tag)).join(', ')}</li>
             {profile.offeredArrangement?.mentoringField && (
                 <li>{t('sugar:offered_mentoring_field')}: {profile.offeredArrangement.mentoringField}</li>
             )}
              <li>{t('sugar:offered_arrangement_type')}: {profile.offeredArrangement?.arrangementType?.map(tag => t(`sugar:tags.arrangement.${tag}`, tag)).join(', ')}</li>
          </ul>
        </div>

        {canContact && (
          <Button onClick={handleContact}>{t('sugar:button_contact')}</Button>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto py-8">
        {isLoading && <Skeleton className="w-3/4 h-8 mb-6" />}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <Terminal className="h-4 w-4" />
            <AlertTitle>{t('common:error_title')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
         {!isLoading && !error && !profile && (
             <Alert variant="default" className="mb-6">
                <Terminal className="h-4 w-4" />
                <AlertTitle>{t('common:not_found')}</AlertTitle>
                <AlertDescription>{t('sugar:error_profile_not_found')}</AlertDescription>
            </Alert>
        )}
        {renderProfileDetails()}
      </div>
    </Layout>
  );
};

export default SugarDaddyDetailPage; 